import { FC } from 'react'
import { Box, IconButton, Link, Typography, useMediaQuery, useTheme } from '@mui/material'
import {
  ArrowBack,
  AttachFileOutlined,
  CheckCircle,
  DescriptionOutlined
} from '@mui/icons-material'
import { alpha } from '@mui/material/styles'
import dayjs from 'dayjs'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import BackNavigationHeader, { BackNavigationContent } from 'components/BackNavigationHeader'
import {
  BookingAgreementAttachment,
  BookingAgreementReceipt,
  LeaseContractsBookingAgreementResponse
} from 'services/contracts/hooks/useLeaseContractsBookingAgreement'
import theme from 'styles/theme'

interface AdditionalServicesBookingAgreementReceiptProps {
  receipt: BookingAgreementReceipt
  metadata?: LeaseContractsBookingAgreementResponse
}

const CARD_SHADOW = '0px 1px 3px rgba(0, 0, 0, 0.06)'

const formatDateTime = (iso: string | null | undefined) => {
  if (!iso) return '—'
  const date = dayjs(iso)
  return date.isValid() ? date.format('DD.MM.YYYY HH:mm') : iso
}

const findAttachmentFile = (
  attachmentId: number,
  metadata?: LeaseContractsBookingAgreementResponse
): BookingAgreementAttachment | undefined =>
  metadata?.attachments?.find((attachment) => attachment.id === attachmentId)

const AdditionalServicesBookingAgreementReceipt: FC<
  AdditionalServicesBookingAgreementReceiptProps
> = ({ receipt, metadata }) => {
  const { formatMessage } = useIntl()
  const navigate = useNavigate()
  const muiTheme = useTheme()
  const isDesktop = useMediaQuery(muiTheme.breakpoints.up('md'))
  const pdfUrl = metadata?.bookingAgreementPdf
  const backTo = '/additional-services'
  const pageTitle = formatMessage({
    id: 'additionalServicesBookingAgreement.signTitle',
    defaultMessage: 'Podpisz umowę'
  })

  const header = isDesktop ? (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mb: '20px' }}>
      <IconButton
        onClick={() => navigate(backTo)}
        sx={{ width: '36px', height: '36px', color: theme.r4r.navy, borderRadius: 0 }}>
        <ArrowBack sx={{ fontSize: '22px' }} />
      </IconButton>
      <Typography
        sx={{ fontSize: '24px', fontWeight: 700, lineHeight: '28.8px', color: theme.r4r.navy }}>
        {pageTitle}
      </Typography>
    </Box>
  ) : null

  const content = (
    <>
      {header}
      <Box
        sx={{
          backgroundColor: '#fff',
          boxShadow: CARD_SHADOW,
          padding: isDesktop ? '40px 32px' : '28px 20px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          mb: '16px'
        }}>
        <Box
          sx={{
            width: '64px',
            height: '64px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: alpha(theme.r4r.green, 0.12),
            mb: '16px'
          }}>
          <CheckCircle sx={{ color: theme.r4r.green, fontSize: '36px' }} />
        </Box>
        <Typography sx={{ fontSize: '18px', fontWeight: 800, color: theme.r4r.navy }}>
          {formatMessage({
            id: 'additionalServicesBookingAgreement.receiptTitle',
            defaultMessage: 'Umowa rezerwacyjna zaakceptowana'
          })}
        </Typography>
        <Typography sx={{ fontSize: '13px', color: theme.r4r.grey, mt: '8px', maxWidth: '480px' }}>
          {formatMessage({
            id: 'additionalServicesBookingAgreement.receiptSubtitle',
            defaultMessage: 'Otrzymasz e-mail z podpisaną umową oraz załącznikami.'
          })}
        </Typography>
      </Box>

      <Box sx={{ backgroundColor: '#fff', boxShadow: CARD_SHADOW, mb: '16px' }}>
        {[
          {
            label: formatMessage({
              id: 'additionalServicesBookingAgreement.receiptId',
              defaultMessage: 'Numer akceptacji'
            }),
            value: String(receipt.id)
          },
          {
            label: formatMessage({
              id: 'additionalServicesBookingAgreement.receiptAcceptedAt',
              defaultMessage: 'Data akceptacji'
            }),
            value: formatDateTime(receipt.acceptedAt)
          },
          {
            label: formatMessage({
              id: 'additionalServicesBookingAgreement.receiptDocumentVersion',
              defaultMessage: 'Wersja dokumentu'
            }),
            value: receipt.contractPdfSha256?.slice(0, 16) || '—'
          }
        ].map((row, index, list) => (
          <Box
            key={row.label}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '12px',
              px: '24px',
              py: '12px',
              borderBottom:
                index === list.length - 1 ? 'none' : `1px solid ${theme.r4r.greyLight}`
            }}>
            <Typography sx={{ fontSize: '13px', color: theme.r4r.grey }}>{row.label}</Typography>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: theme.r4r.ink }}>
              {row.value}
            </Typography>
          </Box>
        ))}
      </Box>

      {pdfUrl && (
        <Link
          href={pdfUrl}
          target='_blank'
          rel='noopener noreferrer'
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            backgroundColor: '#fff',
            boxShadow: CARD_SHADOW,
            padding: '16px 24px',
            textDecoration: 'none',
            mb: '16px'
          }}>
          <DescriptionOutlined sx={{ fontSize: '20px', color: theme.r4r.navy }} />
          <Typography sx={{ flex: 1, fontSize: '14px', fontWeight: 600, color: theme.r4r.navy }}>
            {formatMessage({
              id: 'additionalServicesBookingAgreement.receiptOpenPdf',
              defaultMessage: 'Otwórz umowę PDF'
            })}
          </Typography>
        </Link>
      )}

      {receipt.attachmentsManifest?.length > 0 && (
        <Box sx={{ backgroundColor: '#fff', boxShadow: CARD_SHADOW }}>
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 700,
              color: theme.r4r.navy,
              px: '24px',
              pt: '16px',
              pb: '8px'
            }}>
            {formatMessage({
              id: 'additionalServicesBookingAgreement.attachments',
              defaultMessage: 'Załączniki'
            })}
          </Typography>
          {receipt.attachmentsManifest.map((item) => {
            const file = findAttachmentFile(item.attachmentId, metadata)
            return (
              <Link
                key={item.attachmentId}
                href={file?.file}
                target={file ? '_blank' : undefined}
                rel={file ? 'noopener noreferrer' : undefined}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  px: '24px',
                  py: '12px',
                  borderTop: `1px solid ${theme.r4r.greyLight}`,
                  textDecoration: 'none',
                  pointerEvents: file ? 'auto' : 'none',
                  opacity: file ? 1 : 0.5
                }}>
                <AttachFileOutlined sx={{ fontSize: '16px', color: theme.r4r.navy }} />
                <Box>
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, color: theme.r4r.ink }}>
                    {item.name}
                  </Typography>
                  {!!item.sha256 && (
                    <Typography sx={{ fontSize: '11px', color: theme.r4r.grey, fontFamily: 'monospace' }}>
                      sha256: {item.sha256.slice(0, 8)}
                    </Typography>
                  )}
                </Box>
              </Link>
            )
          })}
        </Box>
      )}
    </>
  )

  if (isDesktop) {
    return content
  }

  return (
    <>
      <BackNavigationHeader label={pageTitle} navigateTo={backTo} />
      <BackNavigationContent>
        <Box pt='env(safe-area-inset-top)'>{content}</Box>
      </BackNavigationContent>
    </>
  )
}

export default AdditionalServicesBookingAgreementReceipt
